import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import cartAnalytics from './cartAnalytics';
import { changeQuantity, changeSize, updateKlarna, updatePaymentMethod } from '../../actions/checkout';
import Price from '../Product/Price';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { translate, prefix } from '../../utils/locale';
import { formatPriceByExample } from '../../utils/string';
import s from './CartItems.scss';
import { productImageSizeTranslation } from '../../utils/image';

class CartItems extends Component {
	async updateQuantityHandler(e) {
		e.preventDefault();
		const { checkout, currentPage, setup } = this.props;
		const $link = e.currentTarget;
		const itemId = $link.closest('.checkoutItem').dataset.item;
		const itemObj = checkout.selection.items.reduce((acc, item) => ({ ...acc, ...(item.item === itemId ? item : {}) }), {});
		const cachedItem = Object.keys(checkout.selectionCache).filter((scItem) => scItem === itemId.split('-')[0]).map((scItem) => checkout.selectionCache[scItem])[0];
		const action = $link.getAttribute('href').slice(1);
		let quantity = itemObj.quantity;

		switch (action) {
			case 'increase':
				quantity += 1;
				break;
			case 'decrease':
				quantity -= 1;
				break;
			case 'remove':
				quantity = 0;
				break;
			default:
				break;
		}

		try {
			if (itemObj.comment) {
				// Find similar products with same comment prefix and loop through change quantity
				const commentPrefix = itemObj.comment.slice(0, 8);
				const updateItems = checkout.selection.items.filter((i) => i.comment.indexOf(commentPrefix) !== -1);
				const response = await Promise.all(updateItems.map((updateItem) => this.props.changeQuantity(updateItem.item.slice(0, updateItem.item.indexOf('-')), updateItem.line, quantity)));

				updateItems.forEach((updateItem, index) => {
					const updateCachedItem = Object.keys(checkout.selectionCache).filter((scItem) => scItem === updateItem.item.split('-')[0]).map((scItem) => checkout.selectionCache[scItem])[0];
					cartAnalytics(updateCachedItem, updateItem.item, setup, false, cachedItem?.category, response[index].data.selection, checkout.listItemCache, ['remove', 'decrease'].indexOf(action) !== -1, quantity);
				});
			} else {
				const response = await this.props.changeQuantity(itemId.slice(0, itemId.indexOf('-')), itemObj.line, quantity);
				cartAnalytics(cachedItem, itemId, setup, false, cachedItem?.category, response.data.selection, checkout.listItemCache, ['remove', 'decrease'].indexOf(action) !== -1, quantity);
			}

			if (currentPage === 'selection') {
				this.props.updateKlarna();
				this.props.updatePaymentMethod();
			}
		} catch (err) {
			Sentry.captureException(err);
			console.log('DEBUG: quantity did not change', err);
		}
	}

	render() {
		const { checkout, currentPage, setup, productCache } = this.props;
		const cache = productCache?.[setup.market]?.[setup.pricelist]?.products || {};

		const selectionItems = (checkout?.selection?.items || []).reduce((acc, item) => {
			if (item.comment) {
				const commentPrefix = item.comment.slice(0, 8);
				const mainItem = checkout.selection.items.filter((i) => i.comment.indexOf(commentPrefix) !== -1 && i.comment.indexOf('services: ') !== -1)[0];
				if (!mainItem) {
					acc[item.line] = item;
					return acc;
				}
				if (!acc[mainItem.line]) acc[mainItem.line] = { subItems: [] };
				if (mainItem.line === item.line) acc[mainItem.line] = { ...item, subItems: acc[mainItem.line].subItems };
				else acc[mainItem.line].subItems.push(item);
			} else {
				acc[item.line] = item;
			}
			return acc;
		}, {});

		const lineItems = Object.keys(selectionItems).map((line) => {
			const item = selectionItems[line];
			const productId = item.item.slice(0, item.item.indexOf('-'));

			let itemDetails = checkout?.selectionCache?.[productId];
			if (!itemDetails && cache) itemDetails = cache[productId];
			if (item.quantity === 0 || !itemDetails) return null;

			const productCategories = itemDetails.categories ? Object.keys(itemDetails.categories).map((c) => itemDetails.categories[c].uri).join('') : '';

			let isAddOnEnabled = false;
			checkout.selection.items.forEach((i) => {
				if (isAddOnEnabled) return;
				const pId = i.item.slice(0, i.item.indexOf('-'));
				let iDetails = checkout.selectionCache[pId];
				if (!iDetails && cache) iDetails = cache[pId];
				if (iDetails?.relatedAddOns && iDetails.relatedAddOns.indexOf(productId) !== -1) isAddOnEnabled = true;
			});

			const price = {};
			Object.keys(item).forEach((priceItem) => {
				if (priceItem.indexOf('price') === 0) price[priceItem.replace('priceEach', 'price')] = item[priceItem];
			});

			if (productCategories.indexOf('accessories') === -1 || setup.campaignType) isAddOnEnabled = false;

			if (isAddOnEnabled) {
				let discount = 0.5;
				if (itemDetails.features) {
					discount = Object.keys(itemDetails.features)
						.filter((d) => {
							if (itemDetails.features?.[d]?.featureId === 'addonDiscount') {
								if (itemDetails.features[d].incPricelist && itemDetails.features[d].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
								if (itemDetails.features[d].incMarket && itemDetails.features[d].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
								if (itemDetails.features[d].incCountry && itemDetails.features[d].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
								return true;
							}
							return false;
						})
						.reduce((acc, d) => (parseInt(itemDetails.features[d].feature, 10) / 100), 0.5);
				}
				price.beforeDiscountAsNumber = price.priceAsNumber;
				price.priceAsNumber *= (1 - discount);
				price.beforeDiscount = formatPriceByExample(price.beforeDiscount, price.beforeDiscountAsNumber);
				price.price = formatPriceByExample(price.price, Math.round(price.priceBeforeDiscountAsNumber * (1 - discount)));
			}

			return (
				<div key={item.item} className={[s.cartItems_item, s[`cartItems_item__${currentPage || 'cart'}`], 'checkoutItem'].join(' ')} data-item={item.item}>
					<span className={s.cartItems_item_name}>{ itemDetails.name }</span>
					<Link className={s.cartItems_item_image} to={prefix(setup, itemDetails.canonicalUri)}>
						{ itemDetails?.image && <LazyImage {...productImageSizeTranslation('', itemDetails?.image[0], false, false, 160)} alt={itemDetails.name} /> }
					</Link>
					<div className={s.cartItems_item_info}>
						<div className={s.cartItems_item_info_details}>
							<span className={s.cartItems_item_info_details_size}>{ translate(setup, 'size') }: { item.size }</span>
							<span className={[s.cartItems_item_info_details_price, (price.showAsOnSale || price.priceAsNumber !== price.priceBeforeDiscountAsNumber || isAddOnEnabled ? s.cartItems_item_info_price__sale : null)].join(' ')}><Price price={price} /></span>
						</div>
						{
							currentPage !== 'receipt' && !item.subItems ? (
								<div className={s.cartItems_item_info_changeQuantity}>
									<a className={s.cartItems_item_info_changeQuantity_button} href="#decrease" onClick={(e) => this.updateQuantityHandler(e)}>
										<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
											<line x1="3" x2="13" y1="8" y2="8" stroke="#2B3337" strokeWidth="2" />
										</svg>
									</a>
									<span className={s.cartItems_item_info_changeQuantity_quantity}>{ item.quantity }</span>
									<a className={s.cartItems_item_info_changeQuantity_button} href="#increase" onClick={(e) => this.updateQuantityHandler(e)}>
										<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
											<g fill="none" strokeLinecap="square">
												<line x1="3" x2="13" y1="8" y2="8" stroke="#2B3337" strokeWidth="2" />
												<line x1="8" x2="8" y1="3" y2="13" stroke="#2B3337" strokeWidth="2" />
											</g>
										</svg>
									</a>
								</div>
							) : <span className={s.cartItems_item_info_changeQuantity_quantity}>{ item.quantity }</span>
						}
					</div>
					{
						item.subItems && item.subItems.length > 0 && (
							<div className={s.cartItems_item_services}>
								{ item.subItems.map((subItem) => {
									const subItemPrice = {};
									Object.keys(subItem).forEach((priceItem) => {
										if (priceItem.indexOf('price') === 0) subItemPrice[priceItem.replace('priceEach', 'price')] = subItem[priceItem];
									});

									return (
										<div key={subItem.item} className={s.cartItems_item_service}>
											<span className={s.cartItems_item_service_name}>{ `${subItem.name}: ${(subItem?.comment || '').slice(10)}` }</span>
											<span className={s.cartItems_item_service_price}><Price price={subItemPrice} /></span>
										</div>
									);
								})}
							</div>
						)
					}
					{
						currentPage !== 'receipt' && (
							<a className={s.cartItems_item_info_changeQuantity_remove} href="#remove" onClick={(e) => this.updateQuantityHandler(e)}>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
									<g fill="none" strokeLinecap="square">
										<line x1="3.455" x2="12.545" y1="3.455" y2="12.545" stroke="#2B3337" strokeWidth="2" />
										<line x1="3.455" x2="12.545" y1="12.545" y2="3.455" stroke="#2B3337" strokeWidth="2" />
									</g>
								</svg>
							</a>
						)
					}
				</div>
			);
		});

		return (
			<div className={s.cartItems}>
				{ checkout.selection.items ? lineItems : <span>No Items</span> }
			</div>
		);
	}
}

const mapState = ({ environment, checkout, productCache }, { currentPage }) => ({ checkout, setup: environment.setup, productCache, currentPage });

export default hot(module)(connect(mapState, { changeQuantity, changeSize, updateKlarna, updatePaymentMethod })(CartItems));
